import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { StyleSheet, Pressable, View, ActivityIndicator, Linking } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useAuthStore, useToastStore, useModalStore } from '@/store';
import copyText from '@/constants/utils/copy_text';
import UpgradeContainer from '@/components/UpgradeContainer';
import Tooltip from '@/components/Tooltip';
import { apiKeysApi } from '@/lib/api';
import getRelativeTime from '@/constants/utils/timestamp';
import { API_DAILY_LIMIT_PLUS_DISPLAY, API_DAILY_LIMIT_PREMIUM_DISPLAY } from '@/constants/config';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';

const ACCENT = '#06B6D4';

const CODE_EXAMPLES = {
  nodejs: `const OpenAI = require('openai');

const client = new OpenAI({
  apiKey: 'YOUR_API_KEY_HERE',
  baseURL: 'https://gab.ai/v1',
});

// Use GET /v1/models to retrieve the current model IDs.
// For example: 'arya' or 'gpt-5-4'
const response = await client.chat.completions.create({
  model: 'arya',
  messages: [
    { role: 'user', content: 'Hello, how are you?' }
  ]
});

console.log(response.choices[0].message.content);`,
  python: `from openai import OpenAI

client = OpenAI(
    api_key = "YOUR_API_KEY_HERE",
    base_url = "https://gab.ai/v1",
)

# Use GET /v1/models to retrieve the current model IDs.
# For example: 'arya' or 'gpt-5-4'
response = client.chat.completions.create(
    model="arya",
    messages=[
        {"role": "user", "content": "Hello, how are you?"}
    ]
)

print(response.choices[0].message.content)`
};

const API_CAPABILITIES = [
  { icon: 'message',     label: 'Chat Completions', endpoint: '/v1/chat/completions' },
  { icon: 'image',       label: 'Image Generation', endpoint: '/v1/images/generations' },
  { icon: 'wand-magic-sparkles', label: 'Image Editing', endpoint: '/v1/images/edit' },
  { icon: 'video',       label: 'Video Generation', endpoint: '/v1/videos/generations' },
  { icon: 'volume-high', label: 'Text-to-Speech',   endpoint: '/v1/audio/speech' },
  { icon: 'vector-square', label: 'Embeddings',     endpoint: '/v1/embeddings' },
  { icon: 'coins',       label: 'Credit Balance',   endpoint: '/v1/credits' },
  { icon: 'key',         label: 'API Key Management', endpoint: '/v1/api-keys' },
  { icon: 'microchip',   label: 'List Models',      endpoint: '/v1/models' },
];

const createStyles = (colors, isDark) => StyleSheet.create({
  container: {
    flex: 1,
    gap: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },

  // Hero card
  heroCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
    padding: 18,
    borderRadius: 14,
    backgroundColor: isDark ? 'rgba(6, 182, 212, 0.06)' : 'rgba(6, 182, 212, 0.04)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.12)',
  },
  heroIconRing: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isDark ? 'rgba(6, 182, 212, 0.14)' : 'rgba(6, 182, 212, 0.10)',
  },
  heroTextBlock: {
    flex: 1,
    gap: 4,
  },
  heroDocsPill: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    marginTop: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    backgroundColor: ACCENT,
  },
  heroDocsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  heroDocsLinkHovered: {
    opacity: 0.85,
  },

  // Sections
  section: {
    gap: 8,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 2,
    marginBottom: 2,
  },
  sectionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  sectionCount: {
    paddingHorizontal: 7,
    paddingVertical: 1,
    borderRadius: 10,
    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
  },

  // API Keys table
  keysTable: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    overflow: 'hidden',
  },
  keysHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    paddingVertical: 9,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    gap: 12,
  },
  keyItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
    gap: 12,
  },
  keyItemLast: {
    borderBottomWidth: 0,
  },
  keyNameCell: {
    width: 120,
    flexShrink: 0,
  },
  keyValueCell: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  keyDateCell: {
    width: 70,
    flexShrink: 0,
  },
  keyActionsCell: {
    width: 70,
    alignItems: 'flex-end',
    flexShrink: 0,
  },
  copyKeyButton: {
    padding: 6,
    borderRadius: 6,
    backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
  },
  revokeButton: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 6,
    backgroundColor: isDark ? 'rgba(239, 68, 68, 0.10)' : 'rgba(239, 68, 68, 0.06)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(239, 68, 68, 0.18)' : 'rgba(239, 68, 68, 0.12)',
  },
  createKeyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 13,
    paddingHorizontal: 12,
    backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
    gap: 8,
  },
  keysEmptyState: {
    padding: 28,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  keysErrorState: {
    padding: 28,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  retryButton: {
    paddingVertical: 7,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
  },
  statusBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginLeft: 8,
    backgroundColor: 'rgba(239, 68, 68, 0.15)',
  },

  // Code block
  codeCard: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    overflow: 'hidden',
  },
  codeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
  },
  tabList: {
    flexDirection: 'row',
  },
  tab: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabActive: {
    borderBottomColor: ACCENT,
  },
  copyCodeButton: {
    padding: 10,
    paddingRight: 14,
  },
  codeContent: {
    padding: 16,
    backgroundColor: isDark ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.02)',
  },
  codeNote: {
    marginTop: 10,
    lineHeight: 18,
  },

  // Capabilities
  capabilityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.015)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    gap: 10,
  },
  capIconWrap: {
    width: 26,
    height: 26,
    borderRadius: 7,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isDark ? 'rgba(6, 182, 212, 0.12)' : 'rgba(6, 182, 212, 0.08)',
    flexShrink: 0,
  },
  capBody: {
    flex: 1,
  },

  // Rate limits
  rateLimitCard: {
    borderRadius: 12,
    padding: 16,
    backgroundColor: isDark ? 'rgba(6, 182, 212, 0.06)' : 'rgba(6, 182, 212, 0.04)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.12)',
  },
  rateLimitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },

  // Footer
  footer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 15,
    padding: 15,
    borderRadius: 10,
    backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
  },
});

function APISettings() {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const router = useRouter();
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isPlusPremium = useAuthStore(state => state.user?.flags?.isPlusPremium);
  const [activeTab, setActiveTab] = useState('nodejs');
  const { addToast } = useToastStore(state => state.actions);
  const { openModal, closeModal } = useModalStore(state => state.actions);
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();

  const [apiKeys, setApiKeys] = useState([]);
  const [isLoadingKeys, setIsLoadingKeys] = useState(true);
  const [keysError, setKeysError] = useState(null);

  // Revoked keys do not count toward the 10-key limit.
  const activeKeyCount = useMemo(
    () => apiKeys.filter(k => k.isActive !== false).length,
    [apiKeys]
  );

  const fetchApiKeys = useCallback(async () => {
    setIsLoadingKeys(true);
    setKeysError(null);
    try {
      const keys = await apiKeysApi.list();
      setApiKeys(keys);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      setKeysError(error.message || 'Failed to load API keys');
    } finally {
      setIsLoadingKeys(false);
    }
  }, []);

  useEffect(() => {
    if (isPlus) fetchApiKeys();
  }, [isPlus, fetchApiKeys]);

  const handleCopyCode = async () => {
    try {
      await copyText(CODE_EXAMPLES[activeTab]);
      addToast(t('settings.api.codeCopied'), 'success');
    } catch (error) {
      console.error('Failed to copy code:', error);
      addToast(t('settings.api.codeCopyFailed'), 'error');
    }
  };

  const handleCreateKey = useCallback(() => {
    openModal('CreateAPIKeyModal', {
      onCreate: async (name) => {
        const result = await apiKeysApi.create(name);
        return result;
      },
      onKeyCreated: () => {
        fetchApiKeys();
        addToast(t('settings.api.keyCreated'), 'success');
      },
    });
  }, [openModal, fetchApiKeys, addToast, t]);

  const handleRevokeKey = useCallback((keyToRevoke) => {
    openModal('ConfirmationModal', {
      title: t('settings.api.revokeKey'),
      message: t('settings.api.revokeKeyMessage'),
      itemName: keyToRevoke.name || t('settings.api.unnamedKey'),
      confirmText: t('settings.api.revoke'),
      cancelText: t('common.cancel'),
      isDanger: true,
      onConfirm: async () => {
        try {
          await apiKeysApi.revoke(keyToRevoke.id);
          addToast(t('settings.api.keyRevoked'), 'success');
          fetchApiKeys();
          closeModal();
        } catch (error) {
          if (error.message?.includes('cannot_delete_current_key') || error.message?.includes('being used')) {
            addToast(t('settings.api.cannotRevokeCurrent'), 'error');
          } else {
            addToast(error.message || t('settings.api.revokeKeyFailed'), 'error');
          }
        }
      },
    });
  }, [openModal, closeModal, fetchApiKeys, addToast, t]);

  const formatDate = (timestamp) => {
    if (!timestamp) return t('settings.api.never');
    const date = typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp);
    return getRelativeTime(date);
  };

  if (!isPlus) {
    return (
      <UpgradeContainer
        alignLeft
        noPadding
        title={t('settings.api.accessTitle')}
        subtitle={t('settings.api.accessSubtitle')}
        features={[
          {
            icon: 'code',
            title: t('settings.api.restfulAccess'),
            description: t('settings.api.restfulAccessDesc')
          },
          {
            icon: 'cube',
            title: t('settings.api.multipleModels'),
            description: t('settings.api.multipleModelsDesc')
          },
          {
            icon: 'gauge-high',
            title: t('settings.api.requestsPerDay'),
            description: t('settings.api.requestsPerDayDesc')
          },
        ]}
        plusTitle={t('settings.api.accessOurApi')}
        plusSubtitle={t('settings.api.upgradeForApi')}
      />
    );
  }

  const renderKeysTable = () => {
    if (isLoadingKeys) {
      return (
        <View style={styles.keysTable}>
          <View style={styles.keysEmptyState}>
            <ActivityIndicator size="small" color={colors.text_color_secondary} />
            <Text size="xs" color="secondary">{t('settings.api.loadingApiKeys')}</Text>
          </View>
        </View>
      );
    }

    if (keysError) {
      return (
        <View style={styles.keysTable}>
          <View style={styles.keysErrorState}>
            <Icon name="circle-exclamation" size={20} color={colors.color_destructive} />
            <Text size="s" color="secondary" style={{ textAlign: 'center' }}>{keysError}</Text>
            <Pressable accessibilityRole="button" style={styles.retryButton} onPress={fetchApiKeys}>
              <Text size="xs" weight="medium" color="primary">{t('settings.api.retry')}</Text>
            </Pressable>
          </View>
        </View>
      );
    }

    return (
      <View style={styles.keysTable}>
        {/* Table header */}
        <View style={[styles.keysHeader, isXS && { display: 'none' }]}>
          <Text size="xxs" color="tertiary" style={[{ textTransform: 'uppercase', letterSpacing: 0.5 }, { width: 120, flexShrink: 0 }]}>
            {t('settings.api.name')}
          </Text>
          <Text size="xxs" color="tertiary" style={{ textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>
            {t('settings.api.key')}
          </Text>
          <Text size="xxs" color="tertiary" style={[{ textTransform: 'uppercase', letterSpacing: 0.5 }, { width: 70, flexShrink: 0 }]}>
            {t('settings.api.created')}
          </Text>
          <View style={styles.keyActionsCell} />
        </View>

        {/* Table body */}
        {apiKeys.length === 0 ? (
          <View style={styles.keysEmptyState}>
            <View style={{
              width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center',
              backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            }}>
              <Icon name="key" size={16} color={colors.text_color_tertiary} />
            </View>
            <Text size="s" color="secondary">{t('settings.api.noApiKeys')}</Text>
          </View>
        ) : (
          apiKeys.map((key, index) => (
            <View
              key={key.id}
              style={[styles.keyItem, isXS && { flexWrap: 'wrap' }, index === apiKeys.length - 1 && styles.keyItemLast]}
            >
              <View style={[styles.keyNameCell, isXS && { width: '100%' }]}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text size="s" weight="medium" color="primary" numberOfLines={1}>
                    {key.name || t('settings.api.unnamedKey')}
                  </Text>
                  {key.isActive === false && (
                    <View style={styles.statusBadge}>
                      <Text size="xxs" color="destructive">{t('settings.api.revoked')}</Text>
                    </View>
                  )}
                </View>
              </View>
              <View style={styles.keyValueCell}>
                <Text size="s" color="secondary" style={{ fontFamily: 'monospace', fontSize: 13 }} numberOfLines={1}>
                  {key.key}
                </Text>
              </View>
              <Text size="xs" color="tertiary" style={styles.keyDateCell}>
                {formatDate(key.createdAt || key.created)}
              </Text>
              <View style={styles.keyActionsCell}>
                {key.isActive !== false && (
                  <Tooltip label={t('settings.api.revokeKey')}>
                    <Pressable accessibilityRole="button" style={styles.revokeButton} onPress={() => handleRevokeKey(key)}>
                      <Text size="xs" color="destructive">{t('settings.api.revoke')}</Text>
                    </Pressable>
                  </Tooltip>
                )}
              </View>
            </View>
          ))
        )}

        {/* Create new key */}
        {activeKeyCount < 10 && (
          <Pressable accessibilityRole="button" style={styles.createKeyButton} onPress={handleCreateKey}>
            <Icon name="plus" size={13} color={ACCENT} />
            <Text size="s" weight="medium" style={{ color: ACCENT }}>
              {t('settings.api.createNewKey')}
            </Text>
          </Pressable>
        )}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {/* Hero card */}
      <View style={styles.heroCard}>
        <View style={styles.heroIconRing}>
          <Icon name="code" size={24} color={ACCENT} />
        </View>
        <View style={styles.heroTextBlock}>
          <View style={!isXS ? { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 } : undefined}>
            <Text size="l" weight="bold" color="primary">
              {t('settings.api.documentation')}
            </Text>
            {!isXS && (
              <Pressable
                style={({ hovered }) => [styles.heroDocsLink, hovered && styles.heroDocsLinkHovered]}
                onPress={() => router.push('/docs/api')}
              >
                {({ hovered }) => (<>
                  <Icon name="book" size={11} color={ACCENT} solid />
                  <Text size="xs" weight="semibold" style={[{ color: ACCENT }, hovered && { textDecorationLine: 'underline' }]}>
                    {t('settings.api.viewApiDocs')}
                  </Text>
                  <Icon name="arrow-right" size={10} color={ACCENT} />
                </>)}
              </Pressable>
            )}
          </View>
          <Text size="xs" color="secondary" style={{ lineHeight: 18 }}>
            {t('settings.api.documentationSubtitle')}
          </Text>
          {isXS && (
            <Pressable style={styles.heroDocsPill} onPress={() => router.push('/docs/api')}>
              <Icon name="book" size={11} color="#fff" solid />
              <Text size="xs" weight="semibold" style={{ color: '#fff' }}>
                {t('settings.api.viewApiDocs')}
              </Text>
              <Icon name="arrow-right" size={10} color="rgba(255,255,255,0.7)" />
            </Pressable>
          )}
        </View>
      </View>

      {/* API Keys */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <View style={styles.sectionHeaderLeft}>
            <Icon name="key" size={13} color={colors.text_color_primary} />
            <Text size="s" weight="semibold" color="primary">
              {t('settings.api.apiKeysCount').replace('%COUNT%', activeKeyCount)}
            </Text>
          </View>

        </View>
        {renderKeysTable()}
        {apiKeys.length > 0 && (
          <Text size="xs" color="tertiary" style={{ paddingHorizontal: 2, lineHeight: 16 }}>
            {t('settings.api.securityNote')}
          </Text>
        )}
      </View>

      {/* Quick Start */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <View style={styles.sectionHeaderLeft}>
            <Icon name="terminal" size={13} color={colors.text_color_primary} />
            <Text size="s" weight="semibold" color="primary">
              {t('settings.api.quickDocumentation')}
            </Text>
          </View>
        </View>
        <View style={styles.codeCard}>
          <View style={styles.codeHeader}>
            <View style={styles.tabList}>
              <Pressable
                style={[styles.tab, activeTab === 'nodejs' && styles.tabActive]}
                onPress={() => setActiveTab('nodejs')}
              >
                <Text size="s" weight={activeTab === 'nodejs' ? 'medium' : 'regular'}
                  color={activeTab === 'nodejs' ? 'primary' : 'secondary'}>
                  Node.js
                </Text>
              </Pressable>
              <Pressable
                style={[styles.tab, activeTab === 'python' && styles.tabActive]}
                onPress={() => setActiveTab('python')}
              >
                <Text size="s" weight={activeTab === 'python' ? 'medium' : 'regular'}
                  color={activeTab === 'python' ? 'primary' : 'secondary'}>
                  Python
                </Text>
              </Pressable>
            </View>
            <Tooltip label={t('settings.api.copyCode')}>
              <Pressable style={styles.copyCodeButton} onPress={handleCopyCode}>
                <Icon name="copy" size={14} color={colors.text_color_secondary} />
              </Pressable>
            </Tooltip>
          </View>
          <View style={styles.codeContent}>
            <Text size="s" color="secondary" style={{ fontFamily: 'monospace', lineHeight: 20 }}>
              {CODE_EXAMPLES[activeTab]}
            </Text>
          </View>
        </View>
        <Text size="xs" color="secondary" style={styles.codeNote}>
          {t('settings.api.apiNote')}
        </Text>
      </View>

      {/* Capabilities */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <View style={styles.sectionHeaderLeft}>
            <Icon name="grid-2" size={13} color={colors.text_color_primary} />
            <Text size="s" weight="semibold" color="primary">
              {t('settings.api.apiCapabilities')}
            </Text>
          </View>
          <View style={styles.sectionCount}>
            <Text size="xxs" weight="semibold" color="secondary">{API_CAPABILITIES.length}</Text>
          </View>
        </View>
        <View style={{ gap: 5 }}>
          {API_CAPABILITIES.map((cap) => (
            <View key={cap.endpoint} style={styles.capabilityItem}>
              <View style={styles.capIconWrap}>
                <Icon name={cap.icon} size={11} color={ACCENT} solid />
              </View>
              <View style={styles.capBody}>
                <Text size="s" weight="medium" color="primary">{cap.label}</Text>
              </View>
              <Text size="xs" color="tertiary" style={{ fontFamily: 'monospace' }}>
                {cap.endpoint}
              </Text>
            </View>
          ))}
        </View>
      </View>

      {/* Rate Limits */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <View style={styles.sectionHeaderLeft}>
            <Icon name="gauge-high" size={13} color={colors.text_color_primary} />
            <Text size="s" weight="semibold" color="primary">
              {t('settings.api.rateLimits')}
            </Text>
          </View>
        </View>
        <View style={styles.rateLimitCard}>
          <View style={styles.rateLimitRow}>
            <View>
              <Text size="xxl" weight="bold" style={{ color: ACCENT }}>
                {isPlusPremium ? API_DAILY_LIMIT_PREMIUM_DISPLAY : API_DAILY_LIMIT_PLUS_DISPLAY}
              </Text>
              <Text size="xs" color="secondary">{t('settings.api.requestsPerDay')}</Text>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <Text size="xs" color="tertiary">{t('settings.api.resetsDaily')}</Text>
              <Text size="s" weight="medium" color="secondary">{t('settings.api.midnightUtc')}</Text>
            </View>
          </View>
          <Text size="xs" color="tertiary" style={{ lineHeight: 17 }}>
            All responses include X-RateLimit-Limit, X-RateLimit-Remaining, and X-RateLimit-Reset headers
            to help you track your usage.
          </Text>
        </View>
      </View>

      {/* Enterprise */}
      <View style={styles.footer}>
        <Icon name="building" size={12} color={colors.text_color_secondary} style={{ marginTop: 2 }} />
        <View style={{ flex: 1 }}>
          <Text size="xs" weight="medium" color="primary" style={{ marginBottom: 3 }}>
            {t('settings.api.enterprise')}
          </Text>
          <Text size="xs" color="secondary" style={{ lineHeight: 18 }}>
            {t('settings.api.enterpriseContact')}{' '}
            <Text size="xs" weight="medium" style={{ color: ACCENT }} onPress={() => Linking.openURL('mailto:ai@gab.com')}>
              ai@gab.com
            </Text>
          </Text>
        </View>
      </View>
    </View>
  );
}

export default React.memo(APISettings);
